import { useState, useRef, useEffect } from 'react'
import {
  Box, Container, Typography, Card, CardContent,
  TextField, IconButton, Chip, Avatar,
  LinearProgress, Button,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import LockIcon from '@mui/icons-material/Lock'

const quickQs = [
  'Will I get married this year?',
  'Is my career going right?',
  'What is my financial future?',
  'Am I compatible with my partner?',
]

const aiReplies = [
  'The stars reveal a very auspicious period for marriage between late 2025 and 2026. Jupiter entering your 7th house is a strong indicator. Look for a water sign — Cancer or Scorpio. Perform Swayamvara Parvathi Homa on a Friday for blessings.',
  'Saturn in your 10th house is testing patience, but this is temporary. A significant career opportunity will present itself between August and October. Your skills are more valuable than your current position reflects.',
  'Jupiter rules wealth in your chart. The next 18 months show steady upward growth. Avoid speculative investments before June. After August a passive income source becomes significant.',
  'Your combination has strong cosmic compatibility. The critical deepening period is November 2025 when Saturn makes a harmonious angle. Chant Om Shukraya Namah on Fridays together.',
]

export default function AskPandit() {
  const [messages, setMessages] = useState([{
    role: 'ai',
    text: 'Namaste! I am Pandit Aryan, your AI-powered Vedic astrologer. Ask me anything about love, career, health, or your destiny. What is on your mind today?',
  }])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [qLeft, setQLeft] = useState(3)
  const [replyIdx, setReplyIdx] = useState(0)
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const send = (text) => {
    const msg = text || input.trim()
    if (!msg || loading || qLeft <= 0) return

    setMessages(prev => [...prev, { role: 'user', text: msg }])
    setInput('')
    setLoading(true)
    setQLeft(prev => prev - 1)

    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'ai',
        text: aiReplies[replyIdx % aiReplies.length],
      }])
      setReplyIdx(prev => prev + 1)
      setLoading(false)
    }, 1500)
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Avatar sx={{
            width: 50, height: 50,
            bgcolor: '#1A1530', fontSize: 24,
            border: '1px solid #3D3580',
          }}>🧘</Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={500} sx={{ color: '#E8E6FF' }}>
              Pandit Aryan
              <Chip label="AI astrologer" size="small"
                sx={{ ml: 1, fontSize: 10, height: 18 }} />
            </Typography>
            <Typography variant="body2" sx={{ color: '#8B85B8' }}>
              30 years of Vedic astrology experience
            </Typography>
            <Typography variant="caption"
              sx={{ color: '#4CAF50', display: 'flex',
                alignItems: 'center', gap: 0.5, mt: 0.3 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%',
                bgcolor: '#4CAF50' }} />
              Available now
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption"
              sx={{ color: '#8B85B8', display: 'block' }}>
              Free left
            </Typography>
            <Typography fontWeight={600} fontSize={20}
              sx={{ color: '#8B7FFF' }}>
              {qLeft}
            </Typography>
            <LinearProgress variant="determinate"
              value={Math.round((3 - qLeft) / 3 * 100)}
              sx={{ width: 60, mt: 0.5 }} />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{
          p: 2, maxHeight: 380,
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 1.5,
        }}>
          {messages.map((m, i) => (
            <Box key={i} sx={{
              display: 'flex',
              justifyContent: m.role === 'user'
                ? 'flex-end' : 'flex-start',
            }}>
              {m.role === 'ai' && (
                <Avatar sx={{
                  width: 28, height: 28, mr: 1, fontSize: 14,
                  bgcolor: '#1A1530', border: '1px solid #3D3580',
                  alignSelf: 'flex-end',
                }}>🧘</Avatar>
              )}
              <Box sx={{
                maxWidth: '80%', px: 2, py: 1.2,
                borderRadius: 3,
                bgcolor: m.role === 'user' ? '#1A2540' : '#1A1530',
                border: m.role === 'user'
                  ? '1px solid #2A3560' : '1px solid #2A2550',
              }}>
                <Typography variant="body2" lineHeight={1.6}
                  sx={{ color: m.role === 'user'
                    ? '#C5D8F0' : '#E8E6FF' }}>
                  {m.text}
                </Typography>
              </Box>
              {m.role === 'user' && (
                <Avatar sx={{
                  width: 28, height: 28, ml: 1,
                  bgcolor: '#1A2540', fontSize: 10, fontWeight: 600,
                  color: '#85B7EB', border: '1px solid #2A4070',
                  alignSelf: 'flex-end',
                }}>You</Avatar>
              )}
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Avatar sx={{
                width: 28, height: 28, fontSize: 14,
                bgcolor: '#1A1530', border: '1px solid #3D3580',
              }}>🧘</Avatar>
              <Box sx={{
                display: 'flex', gap: 0.6, px: 2, py: 1.2,
                bgcolor: '#1A1530', borderRadius: 3,
                border: '1px solid #2A2550',
              }}>
                {[0, 0.2, 0.4].map((d, i) => (
                  <Box key={i} sx={{
                    width: 6, height: 6,
                    borderRadius: '50%', bgcolor: '#534AB7',
                    animation: 'blink 1.2s infinite',
                    animationDelay: `${d}s`,
                    '@keyframes blink': {
                      '0%,60%,100%': { opacity: 0.2 },
                      '30%': { opacity: 1 },
                    },
                  }} />
                ))}
              </Box>
            </Box>
          )}
          <div ref={bottomRef} />
        </CardContent>
      </Card>

      {qLeft === 0 && (
        <Card sx={{
          mb: 2, border: '1px solid #3D3580 !important',
          textAlign: 'center',
        }}>
          <CardContent sx={{ py: 3 }}>
            <LockIcon sx={{ color: '#8B7FFF', fontSize: 28, mb: 1 }} />
            <Typography fontWeight={500}
              sx={{ color: '#A89EFF', mb: 0.5 }}>
              Free questions used
            </Typography>
            <Typography variant="body2"
              sx={{ color: '#8B85B8', mb: 2 }}>
              Upgrade for unlimited guidance from Pandit Aryan
            </Typography>
            <Button variant="contained" href="/pricing"
              sx={{ borderRadius: 5 }}>
              Upgrade to Pro — ₹299/month
            </Button>
          </CardContent>
        </Card>
      )}

      <Box sx={{
        display: 'flex', gap: 1,
        flexWrap: 'wrap', mb: 2,
      }}>
        {quickQs.map(q => (
          <Chip key={q} label={q} clickable
            onClick={() => send(q)}
            disabled={qLeft === 0}
            sx={{
              fontSize: 11,
              '&:hover': { bgcolor: '#1E1A3A !important' },
            }} />
        ))}
      </Box>

      <Card>
        <CardContent sx={{
          p: '10px !important',
          display: 'flex', gap: 1, alignItems: 'center',
        }}>
          <TextField fullWidth size="small" variant="outlined"
            placeholder={qLeft > 0
              ? `Ask anything... (${qLeft} free left)`
              : 'Upgrade to keep asking'}
            value={input}
            disabled={qLeft === 0}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            sx={{ '& fieldset': { border: 'none' } }} />
          <IconButton onClick={() => send()}
            disabled={qLeft === 0 || loading || !input.trim()}
            sx={{
              bgcolor: '#534AB7', color: 'white',
              width: 36, height: 36,
              '&:hover': { bgcolor: '#3C3489' },
              '&:disabled': { bgcolor: '#1E1A3A', color: '#4A4575' },
            }}>
            <SendIcon fontSize="small" />
          </IconButton>
        </CardContent>
      </Card>

      <Typography variant="caption"
        sx={{ display: 'block', textAlign: 'center',
          mt: 1.5, color: '#4A4575' }}>
        Powered by AI · Always transparent · Not a real astrologer
      </Typography>

    </Container>
  )
}